import React, { useEffect, useRef, useState } from "react";
import { Avatar } from "@mui/material";
import Post from "../../components/Post";
import axios from "../../MyAxios";
import { useDispatch, useSelector } from "react-redux";
import {
  CREATE_POST,
  GET_POSTS,
  GET_SAVED_POSTS_ID,
} from "../../constants/api";
import Layout from "../../Layout";
import Loader from "../../components/Loader";
import { Navigate, useNavigate } from "react-router-dom";
import { setUserRecord } from "../../redux/userSlice";
import useLoggedIn from "../../hooks/useLoggedIn";
import addPost2 from "../../assets/img/addPost2.svg";
import { useTheme } from "@emotion/react";
import {
  removeFromPosts,
  resetPosts,
  setClickedPostId,
  setPage,
  setPosts,
  setTotalPages,
} from "@redux/prevPostsSlice";
import { clearGroups, setIsGroupOnlyPost } from "@redux/postSlice";
import Button from "../../components/Button";
import { useGeolocation } from "../../hooks/useGeolocation";
import SecondaryButton from "@components/SecondaryButton";

function HomePage() {
  // useLoggedIn();

  const firstRender = useRef(true);

  const [voteStatuses, setVoteStatuses] = useState({});
  const [postsLoading, setPostsLoading] = useState(false);
  const [nextPageLoading, setNextPageLoading] = useState(false);
  const [resurfacedEventIds, setResurfacedEventIds] = useState(null);
  const [observerTarget, setObserverTarget] = useState(null);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [isCompactView, setIsCompactView] = useState(true);
  const observerRef = useRef(null);
  const observerItemRef = React.useRef(null);
  const POSTS_PER_PAGE = 8;
  const [selectedGroups, setSelectedGroups] = useState(
    localStorage.homepageGroups ? JSON.parse(localStorage.homepageGroups) : [],
  );
  const [selectedCommunity, setSelectedCommunity] = useState(
    localStorage.homepageCommunity,
  );
  const [savedItems, setSavedItems] = useState([]);
  const [getSaved, setGetSaved] = useState(0);
  const { requestGeolocation } = useGeolocation();
  const { posts, page, totalPages, clickedPostId } = useSelector(
    (state) => state.prevPosts,
  );
  const post = useSelector((state) => state.post);
  const { userRecord, filterSelectedTags, filterFromDate, filterToDate, filterContentType, filterPostType } =
    useSelector((state) => state.user);
  const isAnonymousUser = !Boolean(userRecord?.id);

  const dispatch = useDispatch();
  const theme = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    if (!posts || posts.length === 0) return;

    const ids = posts
      .map((p) => {
        const isEvent =
          p.startTime ||
          p.endTime ||
          p.eventType ||
          String(p.id).startsWith("event-");

        return isEvent
          ? (String(p.id).startsWith("event-") ? p.id : `event-${p.id}`)
          : p.id;
      })
      .filter(Boolean);

    if (ids.length === 0) return;


    axios
      .post("/post/vote-status-bulk", {
        ids,
      })
      .then((res) => {
        setVoteStatuses((prev) => ({
          ...prev,
          ...res.data,
        }));
      })
      .catch((err) => {
        console.error("Failed to fetch bulk vote status", err);
      });

  }, [posts]);

  // TODO: We can have a loop here if the content is too much and 300 milliseconds is not enough
  useEffect(() => {
    const interval = setInterval(() => {
      let postElement;
      if (clickedPostId) {
        postElement = document.getElementById(clickedPostId);
      }
      if (postElement) {
        clearInterval(interval);
        postElement.scrollIntoView({ behavior: "smooth" });
        dispatch(setClickedPostId(""));
      }
    }, 100);
  }, [clickedPostId]);

  const removePost = (id) => {
    dispatch(removeFromPosts(id));
  };

  const GetPosts = async (page, callback, { showLoader = true } = {}) => {
    try {
      if (showLoader) {
        setPostsLoading(true);
      }

      // Request geolocation before fetching posts
      let locData = userLocation;
      if (!locData) {
        locData = await requestGeolocation();
        if (locData) {
          setUserLocation(locData);
          window.console.log("📍 [HomePage] User location set:", locData);
        }
      }

      const params = {
        selectedCommunity,
        selectedGroups,
        selectedTags: filterSelectedTags,
        fromDate: filterFromDate,
        toDate: filterToDate,
        contentType: filterContentType,
        postType: filterPostType,
        limit: 8,
        // for consistent event pagination
        resurfacedEventIds: resurfacedEventIds || undefined,
      };

      // Add geolocation params if available
      if (locData) {
        params.userLat = locData.lat;
        params.userLng = locData.lng;
        params.userAccuracy = locData.accuracy;
        window.console.log(
          "📍 [HomePage] Sending geolocation with request:",
          params,
        );
      }

      await axios
        .get(`${GET_POSTS}/${page}`, {
          params,
        })
        .then((res) => {
          callback?.();
          dispatch(setPosts(res.data.posts));
          dispatch(setTotalPages(res.data.totalPages));
          setResurfacedEventIds(res.data.resurfacedEventIds);
          if (showLoader) {
            setPostsLoading(false);
          }
        });
    } catch (error) {
      console.error(error);
      if (showLoader) {
        setPostsLoading(false);
      }
    }
  };

  const fetchNextPage = React.useCallback(async () => {
    if (postsLoading || nextPageLoading || page >= totalPages) return;
    const nextPage = page + 1;
    setNextPageLoading(true);
    dispatch(setPage(nextPage));
    try {
      await GetPosts(nextPage, undefined, { showLoader: false });
    } finally {
      setNextPageLoading(false);
    }
  }, [dispatch, GetPosts, nextPageLoading, page, postsLoading, totalPages]);

  useEffect(() => {
    const handleScroll = () => {
      if (!hasScrolled) {
        setHasScrolled(true);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (
      !observerTarget ||
      !hasScrolled ||
      page >= totalPages ||
      postsLoading ||
      nextPageLoading
    )
      return;

    const observer = new IntersectionObserver(
      async (entries) => {
        if (entries[0]?.isIntersecting) {
          observer.disconnect();
          if (observerRef.current) {
            observerRef.current.disconnect();
          }
          observerRef.current = null;
          setObserverTarget(null);

          const item = observerItemRef.current;
          const isResurfacedEvent =
            item?.isResurfacedContent &&
            !!(
              item?.startTime ||
              item?.endTime ||
              item?.eventType ||
              item?.attendeesFrom ||
              item?.admissionFeeType ||
              (typeof item?.id === "string" &&
                String(item.id).startsWith("event-"))
            );

          if (isResurfacedEvent) {
            const eventId =
              typeof item.id === "string" && item.id.startsWith("event-")
                ? item.id.replace("event-", "")
                : item.id;
            const communityId =
              item.groups?.[0]?.community?.id ||
              item.groups?.[0]?.community ||
              item.communities?.[0]?.id;

            if (communityId && eventId) {
              try {
                await axios.post("/engagement/mark-viewed", {
                  contentId: eventId,
                  contentType: "event",
                  communityId,
                });
              } catch (err) {
                console.error("Failed to mark resurfaced view:", err);
              }
            }
          }

          fetchNextPage();
        }
      },
      {
        rootMargin: "0px 0px 800px 0px",
        threshold: 0,
      },
    );

    observerRef.current = observer;
    observer.observe(observerTarget);

    return () => {
      observer.disconnect();
    };
  }, [
    observerTarget,
    page,
    totalPages,
    postsLoading,
    nextPageLoading,
    fetchNextPage,
  ]);

  const handleCommunityChange = (comId) => {
    setSelectedCommunity(comId);

    if (comId) {
      handleGroupChange([]);
      localStorage.setItem("homepageCommunity", comId);
    } else {
      localStorage.removeItem("homepageCommunity");
    }
  };

  const handleGroupChange = (groupIds) => {
    setSelectedGroups(groupIds);

    if (groupIds?.length) {
      // handleCommunityChange();
      localStorage.setItem("homepageGroups", JSON.stringify(groupIds));
    } else {
      localStorage.removeItem("homepageGroups");
    }
  };

  useEffect(() => {
    if (page === 1 && posts.length === 0) {
      GetPosts(page, () => (firstRender.current = false));
    }
  }, [page]);

  useEffect(() => {
    // If homepage rendered by clicking back icon, we don't call any api but disable first render to enable community toggling
    if (firstRender.current && posts.length > 0) {
      firstRender.current = false;
      return;
    }

    if (!firstRender.current && !clickedPostId) {
      let currPage = 1;

      dispatch(setPage(currPage));
      GetPosts(currPage, () => dispatch(resetPosts()));
      document.body.scrollIntoView();
    }
  }, [
    selectedCommunity,
    selectedGroups,
    filterSelectedTags,
    filterFromDate,
    filterToDate,
    filterContentType,
    filterPostType,
  ]);

  useEffect(() => {
    if (!clickedPostId) {
      dispatch(resetPosts());
      dispatch(setPage(1));
    }
  }, []);

  if (localStorage.loginRedirect) {
    let redirectPath = localStorage.loginRedirect;
    localStorage.removeItem("loginRedirect");
    
    let redirectCondition = localStorage.loginRedirectCondition;
    localStorage.removeItem("loginRedirectCondition");
    if(redirectCondition === "NOT-ANON") {
      return isAnonymousUser? window.location.reload() : <Navigate to={redirectPath} />;
    }
    
    return <Navigate to={redirectPath} />;
  }

  const postingIsDisabled = !Boolean(
    userRecord?.joinedCommunities?.some((com) => com.disabledAt === 0),
  );

  return (
    <Layout
      filters
      handleGroupChange={(groupId) => handleGroupChange(groupId)}
      handleCommunityChange={(comId) => handleCommunityChange(comId)}
      isCompactView={isCompactView}
      setIsCompactView={setIsCompactView}
    >
      <div
        id="top-page"
        style={{
          opacity: postsLoading ? 0.5 : 1,
          minHeight: `calc(100vh - 224px)`,
          paddingBottom: isAnonymousUser? "120px" : null,  // for "Log In" and "Join Now" buttons
        }}
      >
        {postsLoading && (!posts || posts.length === 0) && (
          <div className="flex justify-center py-6">
            <Loader />
          </div>
        )}

        {!postsLoading && (!posts || posts.length == 0) && <div className="my-10 text-center italic">No Posts Found</div>}

        {(!postsLoading || (posts && posts.length > 0)) &&
          posts?.map((item, key) => {
            // resurfaced events are stored in the frontend state for consistently showing the same events on different filters
            // so manually check if it shouldn't be displayed 
            const isEvent = item?.startTime || item?.eventType || String(item.id).startsWith("event-");
            if (filterPostType === "standard" && isEvent) {
              return null;
            }

            const observerIndex = page * POSTS_PER_PAGE - 4;
            const isObserverTarget = hasScrolled && key === observerIndex;
            if (isObserverTarget) observerItemRef.current = item;
            let postGroups = item?.groups;

            // Only show posts with groups that user shares
            if (Boolean(postGroups?.length)) {
              postGroups = postGroups.filter(
                (group) =>
                  userRecord?.groups?.some(
                    (userGroup) => userGroup.id == group.id,
                  ) ||
                  userRecord?.groupsAdmin?.some(
                    (userGroup) => userGroup.id == group.id,
                  ) ||
                  userRecord?.groupsModerated?.some(
                    (userGroup) => userGroup.id == group.id,
                  ),
              );
            }

            const postCommunity = item?.communities?.[0];
            const userCanRemoveCommunityPost =
              postCommunity?.adminAccessLevels?.canDeleteCommunityPost &&
              item.groups?.length === 0 &&
              postCommunity?.admin === userRecord?.id;

            if (postGroups?.length > 0) {
              return (
                <div
                  key={key}
                  ref={isObserverTarget ? setObserverTarget : null}
                >
                  {postGroups
                    .filter((g) => g?.deletedAt === 0 && g?.disabledAt === 0)
                    .map((group, index) => (
                      <div
                        id={`group-${group?.id}-${item?.id}`}
                        key={index}
                        style={{
                          borderColor: isCompactView? undefined : theme.palette.primary.main,
                          scrollMarginTop: 110,
                        }}
                        className={isCompactView? undefined : "border-b-2"}
                      >
                        <Post
                          group={group}
                          post={item}
                          voteStatus={
                            voteStatuses[
                            isEvent
                              ? `event-${item.id.replace("event-", "")}`
                              : item.id
                            ]
                          }
                          savedItems={savedItems}
                          setGetSaved={setGetSaved}
                          getSaved={getSaved}
                          comment={true}
                          removePost={() => removePost(item?.id)}
                          isCompact={!isAnonymousUser && isCompactView}
                        />
                      </div>
                    ))}
                </div>
              );
            } else {
              return (
                <div
                  id={`post-${item.id}`}
                  key={key}
                  ref={isObserverTarget ? setObserverTarget : null}
                  style={{
                    borderColor: isCompactView? undefined : theme.palette.primary.main,
                    scrollMarginTop: 110,
                  }}
                  className={isCompactView? undefined : "border-b-2"}
                >
                  <Post
                    post={item}
                    voteStatus={
                      voteStatuses[
                      isEvent
                        ? `event-${item.id.replace("event-", "")}`
                        : item.id
                      ]
                    }
                    savedItems={savedItems}
                    setGetSaved={setGetSaved}
                    getSaved={getSaved}
                    comment={true}
                    removePost={() => removePost(item.id)}
                    canRemoveCommunityPost={userCanRemoveCommunityPost}
                    isCompact={!isAnonymousUser && isCompactView}
                  />
                </div>
              );
            }
          })}
        {page < totalPages && Boolean(posts?.length) && (
          <div
            className="my-2 text-center mx-4"
            onClick={() => {
              if (postsLoading || nextPageLoading) return;
              const nextPage = page + 1;
              setNextPageLoading(true);
              dispatch(setPage(nextPage));
              GetPosts(nextPage, undefined, { showLoader: false }).finally(
                () => setNextPageLoading(false),
              );
            }}
          >
            {nextPageLoading ? (
              <div className="flex justify-center py-4">
                <Loader />
              </div>
            ) : (
              <div style={{ minHeight: 44 }} />
            )}
          </div>
        )}
      </div>
      {/* {(!postingIsDisabled && Boolean(posts?.length)) && (
        <div
          onClick={() => {
            if (post.isGroupOnlyPost) {
              dispatch(clearGroups());
              dispatch(setIsGroupOnlyPost(false));
            }
            navigate(CREATE_POST);
          }}
          className="sticky bottom-10 ml-auto mr-2"
        >
          <Avatar className="h-12 w-12 md:h-20 md:w-20" src={addPost2} />
        </div>
      )} */}
      {isAnonymousUser && 
      <div className="fixed bottom-0 left-0 right-0 z-[50] mx-auto flex w-full flex-col md:w-1/2 justify-center items-center">
        <div
          style={{ maxWidth: "700px" }}
          className="flex justify-center py-8 px-2 w-full gap-2 md:gap-4"
        >
          <div className="w-full" onClick={() => navigate("/login-details")}>
            <Button>Login</Button>
          </div>
          <div className="w-full" onClick={() => navigate("/username")}>
            <SecondaryButton>Join Now</SecondaryButton>
          </div>
        </div>
      </div>}
    </Layout>
  );
}

export default HomePage;
